import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Box, Grid } from '@mui/material';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import { FormModal } from 'components/molecules/Modal';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { Resolver, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useAddDecisionFormSchema from 'pages/CaseManagementPage/CasesPage/CaseDetailsPage/AddDecisionForm/schemaValidation';
import { useDictionary } from 'models/dictionaryContext';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useCase } from '../../hooks';
import { EDecisionTypes, IDecisionFormValues } from 'models/cases';

const initialFormValues: IDecisionFormValues = {
  decision: '',
  reasonUniqueIds: [],
  comment: '',
};

interface IAddDecisionForm {
  caseId: string;
  close: () => void;
  isOpen: boolean;
  updateData: () => void;
}

const AddDecisionForm = ({ caseId, close, isOpen, updateData }: IAddDecisionForm) => {
  const { t } = useTranslations();
  const { handleAddDecision } = useCase();
  const {
    caseApprovalReason,
    caseRefusalReason,
    getCaseApprovalReason,
    getCaseRefusalReason,
  } = useDictionary();
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const { schema } = useAddDecisionFormSchema();
  const { clearAlert, modalAlert } = useAlert();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<IDecisionFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<IDecisionFormValues>,
  });

  const decision = useWatch({ control, name: 'decision' });
  const isApproved = decision === EDecisionTypes.approved;
  const reasonOptions = mapDictionaryToOptionProp(
    isApproved ? caseApprovalReason : caseRefusalReason,
  );

  useEffect(() => {
    if (!caseApprovalReason) {
      getCaseApprovalReason();
    }
    if (!caseRefusalReason) {
      getCaseRefusalReason();
    }
  }, []);

  useEffect(() => {
    setValue('reasonUniqueIds', []);
  }, [decision]);

  const onSubmit = async (data: IDecisionFormValues) => {
    if (isSubmitLoading) return;
    setSubmitLoading(true);
    try {
      const res = await handleAddDecision(caseId, data.decision === EDecisionTypes.approved, {
        reasonUniqueIds: data.reasonUniqueIds,
        comment: data.comment,
      });
      if (res) {
        reset(initialFormValues);
        close();
        clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
        await updateData();
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormModal
        open={isOpen}
        onClickClose={() => {
          close();
          clearAlert(ActionTypes.CLEAR_MODAL_ALERT);
          reset(initialFormValues);
        }}
        onClickApprove={handleSubmit(onSubmit)}
        title={t('caseManagement.cases.addDecision.title')}
        titleLeft
      >
        <Grid sx={{ gap: 2, mb: 2.5 }}>
          <Grid size={{ xs: 12 }}>
            <FormRadioGroup
              name="decision"
              label={t('caseManagement.cases.decision.label')}
              options={[
                {
                  label: t('caseManagement.cases.decisionApproved.label'),
                  value: EDecisionTypes.approved,
                },
                {
                  label: t('caseManagement.cases.decisionRefused.label'),
                  value: EDecisionTypes.refused,
                },
              ]}
              control={control}
              errors={errors}
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormMultiSelect
              name="reasonUniqueIds"
              label={t('caseManagement.cases.decisionReason.label')}
              options={reasonOptions}
              setValue={setValue}
              control={control}
              errors={errors}
              disabled={!decision}
              searchable={false}
              required
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormInput
              name="comment"
              type={EFormInputTypes.textarea}
              label={t('caseManagement.cases.decisionComment.label')}
              setValue={setValue}
              control={control}
              errors={errors}
              multiline
              minRows={5}
              charsLimit={1000}
              showCounter
              required
              id="add_decision_form_decision_comment-input"
            />
          </Grid>
        </Grid>
        {modalAlert && (
          <Grid size={{ xs: 12 }}>
            <Box>
              <Alert text={modalAlert.text} variant={modalAlert.variant} />
            </Box>
          </Grid>
        )}
      </FormModal>
    </form>
  );
};

export default AddDecisionForm;
